import React, { useState, useEffect } from "react";
import PageBackButton from "../../components/PageBackButton";
import styled from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Colors } from "../../config";
import VideoTutorials from "../../components/VideoTutorial";
const AccDown = require("../../assets/images/acc_down.png");
const AccRight = require("../../assets/images/acc_right.png");

const btFaqs = [
  {
    // 1
    que: "Overview",
    ans: "PowerFly has been enhanced to support Business Trip (BT) requests. Employees can now apply for Business Trip leave, per diem, and travel request in a single request through PowerFly.",
  },
  {
    // 2
    que: "What has changed?",
    ans: "Earlier, Business Trip leave and per diem were applied through SAP as separate requests and travel was coordinated separately. Going forward, SAP will no longer be used to apply for Business Trip leave or per diem. PowerFly is the single platform for submitting and managing Business Trip requests.",
  },
  {
    // 3
    que: "Where will approvals be visible?",
    ans: "The approval workflow (DOA) will be visible in both PowerFly and SAP. PowerFly remains the primary system for submission and tracking.",
  },
  {
    // 4
    que: "How do I submit a Business Trip request?",
    ans: "Employees must submit one consolidated request in PowerFly that includes leave dates, travel details, per diem request, visa, hotel, rent-a-car requirements, etc. All components will follow a single approval workflow.",
  },
  {
    // 5
    que: "Can I edit my Business Trip request?",
    ans: "Yes. Employees can edit their Business Trip request directly in PowerFly. Any edit will trigger a fresh approval workflow.",
  },
  {
    // 6
    que: "What happens if I cancel my Business Trip?",
    ans: "Employees can initiate cancellation directly in PowerFly. Cancellation requests will follow approvals and may result in no charges, partial refund, full refund, or non-refundable outcomes depending on service usage and vendor rules – directly posted to SAP Finance.",
  },
  {
    // 7
    que: "Do I still need to raise Ask HR ticket for HRIS support to cancel or edit my trip?",
    ans: "No. Employees can independently edit or cancel Business Trip requests in PowerFly. All actions are approval-driven and auditable.",
  },
  {
    // 8
    que: "How are per diem changes handled?",
    ans: "Any change to travel or leave dates automatically updates per diem details within the same request and triggers re-approval.",
  },
  {
    // 9
    que: "How do I request an exception (e.g. If I have received an exception for Business Class travel)?",
    ans: "Travel options are shown on PowerFly based on entitlement. You may add further details/specifics to the trip using the 'Any Special Request' section to Joury for further review.",
  },
  {
    // 10
    que: "Is PowerFly the only tool for Business Trips?",
    ans: `Yes. All Business Trip leave, per diem, and travel requests must be submitted through PowerFly. Employees will no longer be able to submit Business Trip leave or per diem requests in SAP. 
This change has been introduced to address the earlier disjointed process, where changes to Business Trip leave dates in SAP did not automatically update per diem. As a result, cancellations and corrections required manual intervention from the HRIS team.
PowerFly now provides a single, integrated request, ensuring that any change to leave or travel dates automatically updates per diem and follows a streamlined approval workflow.`,
  },
  {
    // 11
    que: "Can my Business Trip (BT) request be raised by my admin, secretary, or HR on my behalf?",
    ans: "No. Business Trip requests in PowerFly must be raised only by the employee who is traveling, using their own PowerFly login. Admins, secretaries, and HR cannot submit BT requests on behalf of employees.",
  },
  {
    // 12
    que: "Does PowerFly support Domestic Business Trips?",
    ans: "Yes. Employees can select Domestic Business Trip while submitting the request. PowerFly will automatically calculate per diem based on the source and destination in line with the approved benefit framework.",
  },
  {
    // 13
    que: "What documents should I prepare before submitting a Business Trip request?",
    ans: "Employees should prepare all relevant trip details before submission, including confirmed travel dates, destination, purpose of travel, in the system: passport validity, visa requirements (if applicable), and any supporting approvals for exceptions. Having complete information helps avoid delays in the approval workflow.",
  },
  {
    // 14
    que: "How early should I submit my Business Trip request?",
    ans: "Employees are encouraged to submit Business Trip requests as early as possible to allow sufficient time for approvals, ticketing, visa processing, and travel arrangements. Late submissions may result in higher travel costs or limited availability.",
  },
  {
    // 15
    que: "Can I submit multiple destinations in one Business Trip request?",
    ans: `     Yes. Employees traveling to multiple destinations within the same trip should include all destinations in a single consolidated request. This ensures accurate travel planning, cost tracking, and per diem calculation. 
Note: Domestic trip and Business trip cannot be combined in a single request.`,
  },
  {
    // 16
    que: "What happens if my Business Trip dates overlap with weekends or public holidays?",
    ans: "PowerFly calculates per diem and leave based on the approved framework. Weekends and public holidays are handled in accordance with company policy and destination rules. Employees should ensure dates are accurately reflected in the request.",
  },
  {
    // 17
    que: "How is per diem calculated in PowerFly?",
    ans: "Per diem is automatically calculated based on the approved benefit framework, considering destination, trip duration, and applicable policies. Employees do not need to manually calculate per diem.",
  },
  {
    // 18
    que: "When will per diem be paid?",
    ans: "Per diem is processed after final approval and aligned with payroll or finance processing cycles, as per company procedures. Employees should ensure timely submission to avoid delays.",
  },
  {
    // 19
    que: "Who should I contact if I face technical issues in PowerFly?",
    ans: "For system-related technical issues that cannot be resolved through PowerFly, employees should contact the HRIS or IT support team through the standard support channel through raised tickets.",
  },
  {
    // 20
    que: "What happens if my Business Trip request is rejected?",
    ans: "If a request is rejected, employees will receive a notification with comments or reasons. They may revise and resubmit the request after addressing the feedback.",
  },
  {
    // 21
    que: "Can I extend my Business Trip after it has started?",
    ans: "Any extension must be submitted in PowerFly as an amendment request and approved through the workflow before additional arrangements are confirmed.",
  },
  {
    // 22
    que: "Are emergency Business Trips handled differently?",
    ans: "Emergency or urgent Business Trips should still be submitted through PowerFly. Employees should notify their manager and relevant stakeholders to expedite approvals.  For urgent inquiries, kindly email Joury team at reservations@jourytravels.com.",
  },
  {
    // 23
    que: "Can I use Business Trip (BT) to book flights for employees on short-term international assignments?",
    ans: "No. Employees on short-term international assignments are not entitled to per diem. Such trips should be treated as Transfers in SAP, and HR will process the bookings offline in coordination with Joury travels.",
  },
  {
    // 24
    que: "Can employees in Kazakhstan request Business Trips through PowerFly?",
    ans: "No. Employees in Kazakhstan will not use PowerFly for Business Trip requests at this time.",
  },
  {
    // 25
    que: "Why is PowerFly not available for Kazakhstan?",
    ans: "Kazakhstan follows a different travel booking process (not using Joury Travels) and has country-specific per diem rates. Therefore, the existing SAP process will continue to apply.",
  },
  {
    // 26
    que: "How should Kazakhstan employees apply for Business Trip leave?",
    ans: "All Business Trip leave requests must continue to be submitted through SAP – Business Trip as per the current process.",
  },
  // 27
  {
    que: "Can I apply for a backdated Business Trip leave?",
    ans: `No. Backdated Business Trip leave requests are not permitted in PowerFly.
All Business Trip requests must be submitted and approved before the travel date. Employees are advised to plan and apply in advance to avoid any delays or non-compliance.`,
  },
];

const faqs = [
  {
    que: `What is Power Fly and what features it has?`,
    ans: `Power Fly is a tool that enables employees to manage their annual leave travel - review available ticket balances and request for flight bookings. The application provides options for booking annual ticket under Company account as per eligibility as well as additional bookings with self-pay option. Future updates will include more features like direct online booking and business trip related bookings. The tool provides a direct and easier channel of communication with Joury travel and automates the travel request experience for a better customer satisfaction.`,
    button: "Click here to access the video tutorial",
  },
  {
    que: `Can I request for business trip ticket on Power Fly?`,
    ans: `You can now request business trips on PowerFly. Simply go to the “Book Business Trip” tab at the top to submit your request. PowerFly is the single platform for submitting and managing Business Trip requests. Going forward, SAP will no longer be used to apply for Business Trip leave or per diem.`,
  },
  {
    que: `Can I book tickets for my family members?`,
    ans: `Eligible dependents are covered under employee account and company paid tickets. For additional dependents outside of the eligibility, you may opt for adding a guest and self-pay.`,
  },
  {
    que: `What is the annual air ticket eligibility for family?`,
    ans: `* Eligibility of family members for annual air tickets are based on grade. * Each employee and eligible dependents, as applicable in below grading classification, are entitled to one round-trip ticket to their home countries only (as per passport). The dependents air ticket eligibility is based on PIH Compensation and Benefits Framework as per employee's grade / contract and as updated on SAP under Dependents section record. Tickets are not transferable to other family members. Dependents must be under the sponsorship of the employee and hold valid Qatar IDs. Staff Category: ▪ Grade 8 to 14 - Economy Class Annually (Employee Only) (Exception to above are Grades 8 to 11 Core Functions in Hospitality Sector - Biennial) ▪ Grade 15 to 17 - Economy Class Annually (Employee + Spouse) ▪ Grade 18 to 20 - Economy Class Annually (Employee + Spouse + Up to 2 Children) ▪ Grade 21 to 24 - Business Class Annually for employee, Dependents Economy Class Annually (Spouse + Up to 2 Children)`,
  },
  {
    que: `Can I add a ticket booking request for a friend?`,
    ans: `Yes, you may add a guest to your booking. This option is only available under Self-Pay category.`,
  },
  {
    que: `How can I pay the flight ticket requested for a friend?`,
    ans: `You will receive a payment link for this after Joury Team provides you with the flight option and booking information is confirmed.`,
  },
  {
    que: `Can I request for salary deduction for additional ticket payment.`,
    ans: `All additional payments shall be settled directly via payment link. We do not provide salary deduction option.`,
  },
  {
    que: `Can I swap my ticket balance between family members?`,
    ans: `Each ticket is allocated to employee and/or a dedicated eligible dependent (spouse or child). Booking can only be requested and done for the dedicated holder of the ticket balance. Names are not interchangeable.`,
  },
  {
    que: `Can I transfer the air ticket to another employee?`,
    ans: `Air tickets can only be utilized by the Employee and his/her dependents (Spouse and or children under 18 years old). Sharing tickets or bookings outside the immediate family (dependents) is strictly not permitted.`,
  },
  {
    que: `Can I use my air ticket for booking to other destinations?`,
    ans: `Effective Sep 2023, all staff requesting annual flight ticket booking on PowerFly have the option to request for alternative destination. When creating a flight ticket request, any destination of choice should be mentioned in the Comment field. Joury Travels will receive and evaluate the request.
Scenario 1 - The alternative designation flight booking will be authorized and covered by the Company if the price for it is lower than the flight to home country. The air ticket credit will be considered utilized and deducted from ticket balance account. It is important to note that selecting a different destination at a lower rate than home country is a flexible option of personal employee choice, hence, this will not lead to any remaining momentary balance or partial ticket for future use.
Scenario 2  - The alternative designation flight booking will be authorized and partially covered by the Company if the price for it is higher than the flight to home country. Employees will receive a payment link to cover the fare difference amount. The air ticket credit will be considered utilized and deducted from ticket balance account.`,
  },
  {
    que: `Why am I only able to see my home country airport?`,
    ans: `When Company paid option is selected home country airports are defaulted a per eligibility and policy. You will be able to select a different country and destination airport and the payment opting will change to Self-Pay.`,
  },
  {
    que: `I am a Palestinian, Ukrainian or Sudanese National - what are my flight booking options?`,
    ans: `You may request for ticket to home country if flights are operating or else submit a request to alternative destination. You will have allotted allowance and Joury Team will revert with flight cost difference. That can be paid and flight booked.`,
  },
  {
    que: `How is my air ticket credited here?`,
    ans: `Air ticket for eligible employees with Grade 8 and above are automatically credited (as round trip Air Ticket to home country) to the employee's SAP account and transferred here to Power Fly on the employment anniversary date to enable booking. 
    In case the employee has any unauthorized absences throughout the year, the credit for this period does not accumulate and the full ticket will accrue in full and mature post the anniversary date once those absences have been compensated to the completion of full work year.`,
  },
  {
    que: `What will happen if I did not use my annual air ticket credits?`,
    ans: `Air tickets are valid for a grace period of 24 months from accrual date (employment anniversary). Any ticket not utilized upon the grace period expiry shall be forfeited. You may monitor here your tickets information, including expiry.`,
  },
  {
    que: `Whose approval is required to apply for air tickets?`,
    ans: `Air ticket request process has been automated through Power Fly. There are no approvals included, however, it is employee responsibility to ensure leave is approved and agreed upon with Line Manager prior requesting flight bookings.`,
  },
  {
    que: `Can I cancel my air ticket booking request?`,
    ans: `Power Fly tool provides option to cancel booking requests at any stage of the process. In case the air ticket has been issued, there might be fees and penalties for change or cancellation/refund. Those charges have to be borne by the employee. If leave was cancelled by the Line Manager for unavoidable operational requirements, that will have to be supported by business justification and manning allocation by the Line Manager and approved by Head of Department. In that case the employee can raise dispute to reimburse the fees on SAP - Benefit reimbursement. Line Manager will have to justify the leave cancellation and why operation cannot be covered or else fees will be borne by him/her. Leave planning shall be used and leave cancellation avoided.`,
  },
  {
    que: `How can I get my air ticket credit back after flight cancellation?`,
    ans: `Your air ticket cancellation request will be processed by Joury team based on your request. Once fees are settled (if any) and cancellation finalized, your air ticket credit will be returned to your Power Fly balance. Your balance will update and appear back into your account after receiving the cancellation request completion notification.`,
  },
  {
    que: `Can I select time travel preferences?`,
    ans: `Yes, you have the option to select Preferred Travel Time in your booking request.`,
  },
  {
    que: `How can I add my frequent flyer information?`,
    ans: `You may provide this information in the Special Request field.`,
  },
  {
    que: `How can I upgrade my flight?`,
    ans: `You may specify this information in the Special Request field. Joury Team will review your eligibility and advice of the fare difference. You can complete the payment difference to obtain the higher class booking.`,
  },
  {
    que: `I followed the installation steps, but I still can't find the app icon on my home screen. What should I do?`,
    ans: `Try restarting your device and checking the app drawer for the app icon. If it still doesn't appear, try reinstalling the app.`,
  },
  {
    que: `I'm getting an error message that says "App installation failed." What should I do?`,
    ans: `Make sure your device is connected to a stable internet connection and has enough storage space. You may also need to clear your browser cache and cookies before attempting to install the app again.`,
  },
  {
    que: `I'm having trouble logging into the app. What should I do?`,
    ans: `Please verify that you are using the correct login credentials. If you're still having trouble, try resetting your PIN or contacting the IT support team for assistance.`,
  },
  {
    que: `Can I use the app on multiple devices?`,
    ans: `Yes, you can log in and use the app on multiple devices as long as you use the same login credentials.`,
  },
  {
    que: `Can I use the app in incognito mode on Chrome and Safari browsers?`,
    ans: `Yes, you can use the app in incognito mode on both Chrome and Safari browsers.`,
  },
  {
    que: `Is there a limit to the number of devices on which I can install the PowerFly app?`,
    ans: `No, there is no limit to the number of devices on which you can install the app.`,
  },
  ,
  {
    que: `Can I use the app without an internet connection?`,
    ans: `No, an internet connection is required to use the app as it relies on real-time data and information.`,
  },
  {
    que: `What should I do if the app crashes or freezes?`,
    ans: `Try force-closing the app and then restarting it. If the problem persists, try uninstalling and reinstalling the app. If the issue still occurs, contact the IT support team.`,
  },
  {
    que: `Can I use the app if I'm not an employee of the company?`,
    ans: `No, the app is only accessible to employees of the company who have been provided with the necessary credentials and permissions.`,
  },
  {
    que: `Can I customize the app to suit my preferences?`,
    ans: `No, the app's interface and features are standardized across all devices and users to ensure consistency and compatibility.`,
  },
  {
    que: `What should I do if I forget my login credentials?`,
    ans: `Contact the IT support team to reset your login credentials and gain access to the app.`,
  },
  {
    que: `Will the app automatically update to newer versions?`,
    ans: `Yes, the app will automatically update to newer versions if automatic updates are enabled on the device. Alternatively, you can manually update the app by uninstalling and installing again..`,
  },
  {
    que: `Can I log in to the app using both my Microsoft account and my Employee ID & PIN?`,
    ans: `No, the app supports two login options: Either with Microsoft account login or with Employee ID & PIN login. Once you choose a login method (either Microsoft account or Employee ID & PIN), you cannot switch between the two methods for the same account.`,
  },
  {
    que: `I have been using the app with my Microsoft account. Can I switch to logging in with my Employee ID & PIN?`,
    ans: `No, you can not switch your login method from Microsoft account to Employee ID & PIN.`,
  },
  {
    que: `I am currently logged in with my Employee ID & PIN. Can I link my Microsoft account to the app?`,
    ans: `No, once you have logged in with your Employee ID & PIN, the app does not provide an option to link or associate a Microsoft account. The app supports a single login method at a time. If you have your Microsoft account from the organization, you will be able to switch to logging in with your Microsoft email.`,
  },
  {
    que: `What if I forget my Employee ID? Can I use my Microsoft account to access the app?`,
    ans: `If you forget your Employee ID, please reach out to your HR department or IT support for assistance in recovering your Employee ID. The app does not support using a Microsoft account as an alternative login option if you forget your Employee ID. You can only do so if you have Microsoft account`,
  },
  {
    que: `What should I do if I forget my PIN?`,
    ans: `If you forget your PIN, you can easily reset it using the "Forgot PIN" feature. This will initiate the PIN reset process and allow you to set a new PIN for accessing the app.`,
  },
  {
    que: `How can I reset my PIN if I forget it?`,
    ans: `To reset your PIN, click on the "Forgot PIN" link on the login screen. A verification code will be sent to your registered mobile number to verify.`,
  },
  {
    que: `How will I receive the verification code?`,
    ans: `The verification code will be sent to the mobile number associated with your account. You should receive the code via SMS.`,
  },
  {
    que: `What should I do once I receive the verification code?`,
    ans: `Once you receive the verification code, enter it in the designated field on the app login screen. This code is used to verify your identity and initiate the PIN reset process.`,
  },
  {
    que: `What happens after I enter the verification code?`,
    ans: `After entering the verification code, you will be prompted to set a new PIN for accessing the app. Follow the on-screen instructions to create a new PIN.`,
  },
  {
    que: `How long is the verification code valid?`,
    ans: `The verification code is typically valid for 20 minutes. It is recommended to enter the code promptly to avoid expiration and the need for re-requesting a new code.`,
  },
  {
    que: `What if I don't receive the verification code?`,
    ans: `If you don't receive the verification code, please ensure that your mobile number is correctly registered with the app. You may also check your spam or junk folders in case the SMS was filtered incorrectly. If the issue persists, contact your organization's IT support or the app's customer support for further assistance.`,
  },
  {
    que: `Can I request a new verification code if the previous one expires or if I didn't receive it?`,
    ans: `Yes, in such cases, you can request a new verification code by clicking on the "Resend Code" on the screen. This will generate and send a new code to your registered mobile number.`,
  },
  {
    que: `Will resetting my PIN affect any other account settings or data?`,
    ans: `Resetting your PIN will not impact any other account settings or data. It is solely for the purpose of accessing the app. Your profile, preferences, and other account information will remain unchanged.`,
  },
  {
    que: `What should I do if I continue to experience issues resetting my PIN?`,
    ans: `If you encounter any difficulties while resetting your PIN using the verification code, we recommend reaching out to your organization's IT support team.`,
  },
  {
    que: `Can I use my Microsoft account on one device and my Employee ID & PIN on another device simultaneously to access the app?`,
    ans: `No, the app only supports one login option at a time. You can either log in with your Microsoft account or your Employee ID & PIN. It is not possible to use both login methods simultaneously on different devices.`,
  },
  {
    que: `I am a new employee and don't have a Microsoft email from the organization yet. How can I log in to the app?`,
    ans: `As a new employee without a Microsoft email, you can log in to the app using your assigned Employee ID and PIN. Once you receive your Microsoft account from the organization, you will be able to switch to logging in with your Microsoft email.`,
  },
  {
    que: `I have been logging in with my Employee ID & PIN. Can I switch to logging in with my Microsoft account?`,
    ans: `Yes, if you have a Microsoft account assigned by the organization, you can switch to logging in with your Microsoft email. Simply go to the app's login screen and choose the "Microsoft Account" option. Enter your Microsoft email and password to log in.`,
  },
  {
    que: `Can I switch back to logging in with my Employee ID & PIN after I have switched to logging in with my Microsoft account?`,
    ans: `No, once you have switched to logging in with your Microsoft account, you cannot reverse the login method and go back to using your Employee ID & PIN. The app supports using either a Microsoft account or an Employee ID & PIN, but not both.`,
  },
  {
    que: `I am having trouble logging in with my Microsoft account. What should I do?`,
    ans: `If you are experiencing difficulties logging in with your Microsoft account, please ensure that you are entering the correct email and password associated with your Microsoft account. If the issue persists, please contact your organization's IT support for further assistance.
`,
  },
];

const FAQs = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [active, setActive] = useState(0);
  const faqLists = [faqs, btFaqs];
  const activeFaqs = faqLists[active];

   const [searchParams, setSearchParams] = useSearchParams();
  const type = searchParams.get("type");

  useEffect(() => {
    if (type === "business") {
      setActive(1);
    } else {
      setActive(0); // default = annual
    }
    setOpenFaq(0);
  }, [type]);

  return (
    <>
      <FaqContainer>
        <PageBackButton
          label="Frequently Asked Questions"
          handleClick={() => navigate(-1)}
        />
        <div
          style={{
            marginTop: "2rem",
            width: "100%",
            display: "flex",
            borderRadius: "1rem",
            overflow: "hidden",
            padding: "0.25rem",
            background: Colors.brandFiveOpacity10,
          }}
        >
          <div
            onClick={() => {
              setActive(0);
              setOpenFaq(0);
              setSearchParams({ type: "annual" });
            }}
            style={{
              flex: 1,
              color: active === 0 ? Colors.brandOne : Colors.white,
              background: active === 0 ? Colors.white : Colors.transparent,
              padding: "1rem",
              textAlign: "center",
              borderRadius: "1rem",
              fontWeight: 700,
            }}
          >
            Annual Trip
          </div>
          <div
            onClick={() => {
              setActive(1);
              setOpenFaq(0);
              setSearchParams({ type: "business" });
            }}
            style={{
              flex: 1,
              color: active === 1 ? Colors.brandOne : Colors.white,
              background: active === 1 ? Colors.white : Colors.transparent,
              padding: "1rem",
              textAlign: "center",
              borderRadius: "1rem",
              fontWeight: 700,
            }}
          >
            Business Trip
          </div>
        </div>
        <FaqListContainer>
          {activeFaqs.map((item, index) => (
            <FaqItem onClick={() => setOpenFaq(index)}>
              <FaqQuestion>
                <span>
                  {index + 1}. {item.que}
                </span>
                <span>
                  <img src={openFaq === index ? AccDown : AccRight} />
                </span>
              </FaqQuestion>
              {openFaq === index && (
                <>
                  <FaqAnswer>{item.ans}</FaqAnswer>
                  <FaqAnswer onClick={() => setShowVideo(true)}>
                    {item.button}
                  </FaqAnswer>
                </>
              )}
            </FaqItem>
          ))}
        </FaqListContainer>
      </FaqContainer>
      {showVideo && (
        <VideoTutorials onDone={() => setShowVideo(false)} fromFaq={true} />
      )}
    </>
  );
};

const FaqItem = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 1rem;
  gap: 1rem;
  display: flex;
  flex-direction: column;
  @media only screen and (max-width: 768px) {
    padding: 1.25rem;
  }
`;
const FaqQuestion = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.25rem;
  color: ${Colors.brandOne};
  font-weight: 500;
  span > img {
    width: 2rem;
  }
  @media only screen and (max-width: 768px) {
    font-size: 1rem;
    span > img {
      width: 1.5rem;
    }
  }
`;
const FaqAnswer = styled.div`
  font-size: 1rem;
  color: ${Colors.brandTwo};
  @media only screen and (max-width: 768px) {
    font-size: 0.9rem;
  }
`;
const FaqContainer = styled.div`
  padding: 3.75rem 2.5rem;
`;
const FaqListContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
  gap: 1rem;
`;
export default FAQs;
